import { apiClient } from '@/lib/apiClient';
import type {
  ApiResponse,
  Project,
  ProjectCreate,
  ProjectUpdate
} from "dico-types";

// ============================================================================
// PROJECT API FUNCTIONS
// ============================================================================

/**
 * List all projects in a workspace.
 * @param workspaceId - The workspace ID.
 * @param signal - Optional abort signal.
 */
const getProjects = (workspaceId: string, signal?: AbortSignal) => {
  return apiClient.get<ApiResponse<Project[]>>(`/api/workspaces/${workspaceId}/projects`, {
    signal
  });
};

/**
 * Get a project by ID.
 * @param workspaceId - The workspace ID.
 * @param projectId - The project ID.
 * @param signal - Optional abort signal.
 */
const getProjectById = (workspaceId: string, projectId: string, signal?: AbortSignal) => {
  return apiClient.get<ApiResponse<Project>>(
    `/api/workspaces/${workspaceId}/projects/${projectId}`,
    { signal }
  );
};

/**
 * Create a new project.
 * @param workspaceId - The workspace ID.
 * @param data - Project creation payload.
 * @param signal - Optional abort signal.
 */
const createProject = (
  workspaceId: string,
  data: Omit<ProjectCreate, 'workspaceId'>,
  signal?: AbortSignal
) => {
  return apiClient.post<ApiResponse<Project>>(
    `/api/workspaces/${workspaceId}/projects`,
    data,
    { signal }
  );
};

/**
 * Update a project.
 * @param workspaceId - The workspace ID.
 * @param projectId - The project ID.
 * @param data - Fields to update.
 * @param signal - Optional abort signal.
 */
const updateProject = (
  workspaceId: string,
  projectId: string,
  data: ProjectUpdate,
  signal?: AbortSignal
) => {
  return apiClient.put<ApiResponse<Project>>(
    `/api/workspaces/${workspaceId}/projects/${projectId}`,
    data,
    { signal }
  );
};

/**
 * Delete a project.
 * @param workspaceId - The workspace ID.
 * @param projectId - The project ID.
 * @param signal - Optional abort signal.
 */
const deleteProject = (workspaceId: string, projectId: string, signal?: AbortSignal) => {
  return apiClient.delete<ApiResponse>(`/api/workspaces/${workspaceId}/projects/${projectId}`, {
    signal
  });
};

export const projectApi = {
  getProjects,
  getProjectById,
  createProject,
  updateProject,
  deleteProject
};

export { getProjects, getProjectById, createProject, updateProject, deleteProject };
